import React, { useState, useCallback, useMemo } from 'react';
import { Link, useForm, usePage, usePoll } from '@inertiajs/react';
import { Download } from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
    Combobox, ComboboxContent, ComboboxEmpty, ComboboxInput, ComboboxItem,
    ComboboxList, ComboboxTrigger, ComboboxValue,
} from '../../components/ui/combobox';
import { Select } from '../../components/ui/select';
import { DatePicker } from '../../components/ui/date-picker';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import Paginator from '../../components/Paginator';
import ValidationError from '../../components/ValidationError';
import { useTrans } from '../../hooks/useTrans';
import { getStatusClass, getStatusText } from './importStatus';

import FileDropzone from './components/FileDropzone';

export default function Index({ availableImporters, debtors, imports, canViewImprovedVersion, pageTranslations }) {
    const translate = useTrans();
    const [activeTab, setActiveTab] = useState(availableImporters[0]?.key || 'general');
    const [debtorDetails, setDebtorDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [dropzoneKey, setDropzoneKey] = useState(0);

    // Poll for running imports
    const hasRunningImports = imports?.data?.some((imp) => !imp.status || imp.status === 'running');
    usePoll(5000, { only: ['imports'] }, { autoStart: hasRunningImports });

    const { data, setData, post, reset, processing, errors } = useForm({
        importer: availableImporters[0]?.key || 'general',
        debtor: '',
        transport_type: '',
        debtor_reference: '',
        debtor_contact: '',
        pickup_date: '',
        order_file: null,
    });

    const fetchDebtorDetails = useCallback((debtorId) => {
        if (!debtorId) {
            setDebtorDetails(null);
            return;
        }

        setLoadingDetails(true);
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '';

        fetch('/debtors/details', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrfToken, 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ name: 'debtor', value: debtorId }),
        })
            .then((res) => res.json())
            .then((details) => {
                setDebtorDetails(details);
                const defaults = details.default || {};
                setData((prev) => ({
                    ...prev,
                    transport_type: defaults.transport_type ? String(defaults.transport_type) : '',
                    debtor_reference: defaults.reference ? String(defaults.reference) : '',
                    debtor_contact: defaults.contact ? String(defaults.contact) : '',
                }));
            })
            .catch(() => setDebtorDetails(null))
            .finally(() => setLoadingDetails(false));
    }, []);

    function handleDebtorChange(item) {
        const value = item?.value ?? '';
        if (value === data.debtor) return;

        setData((prev) => ({
            ...prev,
            debtor: value,
            transport_type: '',
            debtor_reference: '',
            debtor_contact: '',
        }));
        fetchDebtorDetails(value);
    }

    function handleTabChange(key) {
        setActiveTab(key);
        setData('importer', key);
    }

    function handleFileChange(file) {
        setData('order_file', file);
    }

    function handleSubmit(e) {
        e.preventDefault();
        post('/orders-opslaan', {
            forceFormData: true,
            onSuccess: () => {
                reset('debtor', 'transport_type', 'debtor_reference', 'debtor_contact', 'pickup_date', 'order_file');
                setDebtorDetails(null);
                setDropzoneKey((prev) => prev + 1);
            },
        });
    }

    const debtorItems = useMemo(() =>
        debtors.map((d) => ({ value: String(d.id), label: d.label })),
    [debtors]);

    const selectedDebtor = debtorItems.find((i) => i.value === String(data.debtor)) ?? null;

    const transportTypeItems = useMemo(() =>
        (debtorDetails?.transport_types || []).map((t) => ({ value: String(t.id), label: t.description })),
    [debtorDetails]);

    const referenceItems = useMemo(() =>
        (debtorDetails?.references || []).map((r) => ({ value: String(r.id), label: r.reference })),
    [debtorDetails]);

    const contactItems = useMemo(() =>
        (debtorDetails?.contacts || []).map((c) => ({ value: String(c.id), label: c.name })),
    [debtorDetails]);

    const selectedTransportType = transportTypeItems.find((i) => i.value === String(data.transport_type)) ?? null;
    const selectedReference = referenceItems.find((i) => i.value === String(data.debtor_reference)) ?? null;
    const selectedContact = contactItems.find((i) => i.value === String(data.debtor_contact)) ?? null;

    const isFormValid = activeTab === 'police'
        ? data.pickup_date && data.order_file
        : data.debtor && data.transport_type && data.debtor_reference && data.debtor_contact && data.order_file;

    return (
        <div className="max-w-[1200px] mx-auto px-4">
            {canViewImprovedVersion && (
                <div className="mb-4 flex items-center justify-between rounded-[3.75px] bg-[#eff6ff] px-2.5 py-2.5 text-base">
                    <div className="flex items-center gap-2">
                        <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 shrink-0 text-[#60a5fa]">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
                        </svg>
                        <span className="text-[#1d4ed8]">{translate('Er is nu ook een verbeterde versie voor het importeren van algemene orders.')}</span>
                    </div>
                    <a href="/orders/imports" className="font-semibold text-[#1d4ed8] no-underline whitespace-nowrap">
                        {translate('Nu proberen')} &rarr;
                    </a>
                </div>
            )}

            <div className="rounded-[3px] border border-[#bbb] bg-white">
                <div className="p-[30px]">
                    <h3 className="text-2xl font-bold text-[#003545] mb-6">
                        {translate('Orders importeren')}
                    </h3>



                    <form onSubmit={handleSubmit}>
                        {/* Tabs */}
                        {availableImporters.length > 1 && (
                            <div className="flex gap-2 mb-6">
                                {availableImporters.map((imp) => (
                                    <button
                                        key={imp.key}
                                        type="button"
                                        onClick={() => handleTabChange(imp.key)}
                                        className={`rounded-[6px] border-2 px-6 py-1.5 text-[14px] font-black cursor-pointer transition-colors ${
                                            activeTab === imp.key
                                                ? 'border-[#003545] bg-[#003545] text-white'
                                                : 'border-[#ededed] bg-transparent text-[#003545] hover:bg-[#ededed]'
                                        }`}
                                    >
                                        {imp.label}
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* General tab */}
                        {activeTab === 'general' && (
                            <div className="space-y-4">
                                <div className="flex flex-col lg:flex-row lg:items-center">
                                    <label className="lg:w-[376px] shrink-0 text-sm font-bold text-[#003545] mb-1 lg:mb-0">
                                        {translate('Debiteur')}&nbsp;<sup>*</sup>
                                    </label>
                                    <div className="flex-1">
                                        <Combobox
                                            items={debtorItems}
                                            value={selectedDebtor}
                                            onValueChange={handleDebtorChange}
                                            itemToStringValue={(item) => item.label}
                                        >
                                            <ComboboxTrigger render={
                                                <Button variant="outline" className="w-full justify-between font-normal rounded-sm border-[#bbb] shadow-none">
                                                    <ComboboxValue placeholder={translate('Selecteer een optie')} />
                                                </Button>
                                            } />
                                            <ComboboxContent>
                                                <ComboboxInput showTrigger={false} placeholder={translate('Zoeken...')} />
                                                <ComboboxEmpty>{translate('Geen resultaten.')}</ComboboxEmpty>
                                                <ComboboxList>
                                                    {(item) => (
                                                        <ComboboxItem key={item.value} value={item}>{item.label}</ComboboxItem>
                                                    )}
                                                </ComboboxList>
                                            </ComboboxContent>
                                        </Combobox>
                                        <ValidationError message={errors.debtor} />
                                    </div>
                                </div>

                                <div className="flex flex-col lg:flex-row lg:items-center">
                                    <label className="lg:w-[376px] shrink-0 text-sm font-bold text-[#003545] mb-1 lg:mb-0">
                                        {translate('Transporttype')}&nbsp;<sup>*</sup>
                                    </label>
                                    <div className="flex-1">
                                        <Combobox
                                            items={transportTypeItems}
                                            value={selectedTransportType}
                                            onValueChange={(item) => setData('transport_type', item?.value ?? '')}
                                            itemToStringValue={(item) => item.label}
                                            disabled={!debtorDetails || loadingDetails}
                                        >
                                            <ComboboxTrigger render={
                                                <Button variant="outline" className="w-full justify-between font-normal rounded-sm border-[#bbb] shadow-none disabled:bg-[#ededed] disabled:opacity-70 disabled:cursor-not-allowed">
                                                    <ComboboxValue placeholder={translate('Selecteer een optie')} />
                                                </Button>
                                            } />
                                            <ComboboxContent>
                                                <ComboboxInput showTrigger={false} placeholder={translate('Zoeken...')} />
                                                <ComboboxEmpty>{translate('Geen resultaten.')}</ComboboxEmpty>
                                                <ComboboxList>
                                                    {(item) => (
                                                        <ComboboxItem key={item.value} value={item}>{item.label}</ComboboxItem>
                                                    )}
                                                </ComboboxList>
                                            </ComboboxContent>
                                        </Combobox>
                                        <ValidationError message={errors.transport_type} />
                                    </div>
                                </div>

                                <div className="flex flex-col lg:flex-row lg:items-center">
                                    <label className="lg:w-[376px] shrink-0 text-sm font-bold text-[#003545] mb-1 lg:mb-0">
                                        {translate('Referentie')}&nbsp;<sup>*</sup>
                                    </label>
                                    <div className="flex-1">
                                        <Combobox
                                            items={referenceItems}
                                            value={selectedReference}
                                            onValueChange={(item) => setData('debtor_reference', item?.value ?? '')}
                                            itemToStringValue={(item) => item.label}
                                            disabled={!debtorDetails || loadingDetails}
                                        >
                                            <ComboboxTrigger render={
                                                <Button variant="outline" className="w-full justify-between font-normal rounded-sm border-[#bbb] shadow-none disabled:bg-[#ededed] disabled:opacity-70 disabled:cursor-not-allowed">
                                                    <ComboboxValue placeholder={translate('Selecteer een optie')} />
                                                </Button>
                                            } />
                                            <ComboboxContent>
                                                <ComboboxInput showTrigger={false} placeholder={translate('Zoeken...')} />
                                                <ComboboxEmpty>{translate('Geen resultaten.')}</ComboboxEmpty>
                                                <ComboboxList>
                                                    {(item) => (
                                                        <ComboboxItem key={item.value} value={item}>{item.label}</ComboboxItem>
                                                    )}
                                                </ComboboxList>
                                            </ComboboxContent>
                                        </Combobox>
                                        <ValidationError message={errors.debtor_reference} />
                                    </div>
                                </div>

                                <div className="flex flex-col lg:flex-row lg:items-center">
                                    <label className="lg:w-[376px] shrink-0 text-sm font-bold text-[#003545] mb-1 lg:mb-0">
                                        {translate('Contactpersoon')}&nbsp;<sup>*</sup>
                                    </label>
                                    <div className="flex-1">
                                        <Combobox
                                            items={contactItems}
                                            value={selectedContact}
                                            onValueChange={(item) => setData('debtor_contact', item?.value ?? '')}
                                            itemToStringValue={(item) => item.label}
                                            disabled={!debtorDetails || loadingDetails}
                                        >
                                            <ComboboxTrigger render={
                                                <Button variant="outline" className="w-full justify-between font-normal rounded-sm border-[#bbb] shadow-none disabled:bg-[#ededed] disabled:opacity-70 disabled:cursor-not-allowed">
                                                    <ComboboxValue placeholder={translate('Selecteer een optie')} />
                                                </Button>
                                            } />
                                            <ComboboxContent>
                                                <ComboboxInput showTrigger={false} placeholder={translate('Zoeken...')} />
                                                <ComboboxEmpty>{translate('Geen resultaten.')}</ComboboxEmpty>
                                                <ComboboxList>
                                                    {(item) => (
                                                        <ComboboxItem key={item.value} value={item}>{item.label}</ComboboxItem>
                                                    )}
                                                </ComboboxList>
                                            </ComboboxContent>
                                        </Combobox>
                                        <ValidationError message={errors.debtor_contact} />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Police tab */}
                        {activeTab === 'police' && (
                            <div className="space-y-4">
                                <div className="flex flex-col lg:flex-row lg:items-center">
                                    <label className="lg:w-[376px] shrink-0 text-sm font-bold text-[#003545] mb-1 lg:mb-0">
                                        {translate('Verzenddatum')}&nbsp;<sup>*</sup>
                                    </label>
                                    <div>
                                        <DatePicker
                                            name="pickup_date"
                                            value={data.pickup_date}
                                            onChange={(e) => setData('pickup_date', e.target.value)}
                                        />
                                        <ValidationError message={errors.pickup_date} />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* File dropzone */}
                        <div className="flex flex-col lg:flex-row lg:items-start mt-4">
                            <label className="lg:w-[376px] shrink-0 text-sm font-bold text-[#003545] mb-1 lg:mb-0 lg:pt-6">
                                {translate('Ordersbestand')}&nbsp;<sup>*</sup>
                            </label>
                            <div className="flex-1">
                                <FileDropzone key={dropzoneKey} onChange={handleFileChange} />
                                <ValidationError message={errors.order_file} />
                            </div>
                        </div>

                        {/* Submit */}
                        <div className="flex flex-col lg:flex-row lg:justify-end gap-2 mt-6">
                            <Button variant="clear" asChild className="w-full lg:w-auto">
                                <a href={`/orders-importeren/sjabloon/${activeTab}`} download>
                                    <Download className="w-4 h-4" />
                                    {translate('Download sjabloon')}
                                </a>
                            </Button>
                            <Button variant="primary" type="submit" disabled={!isFormValid || processing} className="w-full lg:w-auto">
                                {translate('Start import')}
                            </Button>
                        </div>
                    </form>

                    <hr className="my-6 border-[#ddd]" />

                    {/* Historie */}
                    <h3 className="text-2xl font-bold text-[#003545] mb-4">
                        {translate('Historie')}
                    </h3>

                    <Table>
                        <TableHeader className="bg-[#ededed] [&_tr]:border-b-2 [&_tr]:border-[#d4d4d4]">
                            <TableRow className="[&_th]:h-auto [&_th]:py-5 [&_th]:px-2.5 [&_th]:text-left [&_th]:text-sm [&_th]:font-normal [&_th]:text-[#003545]">
                                <TableHead>{translate('Aangemaakt')}</TableHead>
                                <TableHead>{translate('Door')}</TableHead>
                                <TableHead>{translate('Debiteur')}</TableHead>
                                <TableHead>{translate('Bestandsnaam')}</TableHead>
                                <TableHead>{translate('Regels')}</TableHead>
                                <TableHead>{translate('Ingelezen')}</TableHead>
                                <TableHead>{translate('Dubbelen')}</TableHead>
                                <TableHead>{translate('Fouten')}</TableHead>
                                <TableHead>{translate('Status')}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {imports?.data?.length > 0 ? imports.data.map((imp, index) => (
                                <TableRow key={imp.id} className={`[&_td]:py-5 [&_td]:px-2.5${index % 2 === 1 ? ' bg-[#f5f5f5]' : ''}`}>
                                    <TableCell>
                                        <Link href={`/orders-importeren/${imp.id}`} className="underline">
                                            {imp.created_at || '-'}
                                        </Link>
                                    </TableCell>
                                    <TableCell>{imp.user_name || '-'}</TableCell>
                                    <TableCell>{imp.is_police ? translate('Politie') : (imp.debtor_name || '-')}</TableCell>
                                    <TableCell>
                                        <a href={`/orders-importeren/${imp.id}/bronbestand`} download className="underline" title={translate('Download bronbestand')}>
                                            {imp.file_name}
                                        </a>
                                    </TableCell>
                                    <TableCell>{imp.row_count}</TableCell>
                                    <TableCell>{imp.row_read_count}</TableCell>
                                    <TableCell>{imp.order_double_count}</TableCell>
                                    <TableCell>{imp.row_error_count}</TableCell>
                                    <TableCell className={getStatusClass(imp.status)}>
                                        {getStatusText(imp.status, pageTranslations)}
                                    </TableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={9} className="py-5 px-2.5 text-center text-[#808080]">
                                        {translate('Geen imports gevonden')}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>

                    <Paginator links={imports?.links} />
                </div>
            </div>
        </div>
    );
}
